import api from './api';
import { idempotencyKeyHeaders } from '../utils/idempotency-headers';
import { parseSubscriptionPlanCatalog } from '../utils/parseSubscriptionPlanCatalog';
import type {
  Subscription,
  SubscriptionPixQrCode,
  CreateSubscriptionInput,
  ChangeSubscriptionInput,
  SubscriptionPlanCatalogFromAPI,
  SubscriptionPlanFromAPI,
  SubscriptionTransactionFromAPI,
  SubscriptionTransactionsPaginatedResponseFromAPI,
  SubscriptionBillingTypeEnum,
} from '../types/subscription';

const BASE_PATH = '/admin/subscription';
const PLANS_PATH = '/subscription-plan';

function normalizeCardPayload(
  data: Pick<
    CreateSubscriptionInput,
    'creditCard' | 'creditCardHolderInfo' | 'creditCardToken' | 'saveCard'
  >
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (data.creditCardToken != null) out.creditCardToken = data.creditCardToken;
  if (data.saveCard != null) out.saveCard = data.saveCard;
  if (data.creditCard && data.creditCardHolderInfo) {
    const month = data.creditCard.expiryMonth.replace(/\D/g, '');
    out.creditCard = {
      ...data.creditCard,
      number: data.creditCard.number.replace(/\D/g, ''),
      expiryMonth: month.length <= 1 ? month.padStart(2, '0') : month.slice(0, 2),
    };
    const mobileOnly = (data.creditCardHolderInfo.mobilePhone ?? '').replace(/\D/g, '');
    const addrDigits = (data.creditCardHolderInfo.addressNumber ?? '').replace(/\D/g, '');
    out.creditCardHolderInfo = {
      ...data.creditCardHolderInfo,
      cpfCnpj: (data.creditCardHolderInfo.cpfCnpj ?? '').replace(/\D/g, ''),
      postalCode: (data.creditCardHolderInfo.postalCode ?? '').replace(/\D/g, '').slice(0, 8),
      mobilePhone: mobileOnly || undefined,
      addressNumber: addrDigits.length > 0 ? addrDigits : '0',
    };
  }
  return out;
}

function normalizeCreatePayload(data: CreateSubscriptionInput): Record<string, unknown> {
  const base: Record<string, unknown> = {
    billingType: data.billingType,
    ...(data.planId != null && { planId: data.planId }),
    ...(data.value != null && { value: data.value }),
    ...(data.cycle != null && { cycle: data.cycle }),
    ...(data.nextDueDate != null && { nextDueDate: data.nextDueDate }),
    ...(data.description != null && { description: data.description }),
    ...(data.useSavedCard != null && { useSavedCard: data.useSavedCard }),
  };
  const card = normalizeCardPayload(data);
  return { ...base, ...card };
}

function normalizeChangePayload(data: ChangeSubscriptionInput): Record<string, unknown> {
  const base: Record<string, unknown> = {
    planId: data.planId,
    ...(data.billingType != null && { billingType: data.billingType }),
    ...(data.establishmentPaymentMethodId != null && {
      establishmentPaymentMethodId: data.establishmentPaymentMethodId,
    }),
  };
  const card = normalizeCardPayload(data);
  return { ...base, ...card };
}

export const subscriptionAPI = {
  getPlanCatalog: async (): Promise<SubscriptionPlanCatalogFromAPI> => {
    const response = await api.get<unknown>(PLANS_PATH);
    return parseSubscriptionPlanCatalog(response.data);
  },

  getLatest: async (): Promise<Subscription | null> => {
    const response = await api.get<Subscription | null>(BASE_PATH);
    return response.data;
  },

  getTransactions: async (
    page = 1,
    limit = 10
  ): Promise<SubscriptionTransactionsPaginatedResponseFromAPI> => {
    const response = await api.get<SubscriptionTransactionsPaginatedResponseFromAPI>(
      `${BASE_PATH}/transactions?page=${page}&limit=${limit}`
    );
    return response.data;
  },

  create: async (data: CreateSubscriptionInput): Promise<Subscription> => {
    const payload = normalizeCreatePayload(data);
    const response = await api.post<Subscription>(BASE_PATH, payload, {
      timeout: 65000,
      headers: idempotencyKeyHeaders(),
    });
    return response.data;
  },

  changePlan: async (data: ChangeSubscriptionInput): Promise<Subscription> => {
    const payload = normalizeChangePayload(data);
    const response = await api.post<Subscription>(`${BASE_PATH}/change`, payload, {
      timeout: 65000,
      headers: idempotencyKeyHeaders(),
    });
    return response.data;
  },

  deletePendingChange: async (): Promise<Subscription> => {
    const response = await api.delete<Subscription>(`${BASE_PATH}/change/pending`);
    return response.data;
  },

  cancel: async (id: string): Promise<Subscription> => {
    const response = await api.patch<Subscription>(`${BASE_PATH}/${id}/cancel`, undefined, {
      headers: idempotencyKeyHeaders(),
    });
    return response.data;
  },

  /** QR atualizado no gateway para assinatura PIX pendente ou inativa por falta de pagamento */
  refreshPixQrCode: async (): Promise<SubscriptionPixQrCode | null> => {
    const response = await api.get<SubscriptionPixQrCode | null>(`${BASE_PATH}/pix-qrcode`);
    return response.data ?? null;
  },

  patchPaymentMethod: async (params: {
    billingType: SubscriptionBillingTypeEnum;
    establishmentPaymentMethodId?: string;
  }): Promise<Subscription> => {
    const response = await api.patch<Subscription>(
      `${BASE_PATH}/payment-method`,
      {
        billingType: params.billingType,
        ...(params.establishmentPaymentMethodId != null &&
        params.establishmentPaymentMethodId !== ''
          ? { establishmentPaymentMethodId: params.establishmentPaymentMethodId }
          : {}),
      },
      {
        timeout: 65_000,
        headers: idempotencyKeyHeaders(),
      }
    );
    return response.data;
  },
};
